/**
 * Committee Service
 * 
 * This service handles all committee-specific API calls.
 * It uses the privateApiClient for authenticated requests.
 */

import { privateApiClient, handleApiError } from '../apiClient';
import API from '../../config/api';

/**
 * Get committee dashboard data
 * @returns {Promise<Object>} Dashboard data including stats and candidates
 */
export const getDashboardData = async () => {
  try {
    // Use the standard evaluations endpoint with committee role filtering handled by backend
    // The backend will filter evaluations based on the committee member's center
    const response = await privateApiClient.get(API.committee.dashboard.get);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch committee dashboard data');
  }
};

/**
 * Get candidates assigned to committee member
 * @param {Object} filters - Optional filters for candidates
 * @returns {Promise<Array>} List of candidates
 */
export const getAssignedCandidates = async (filters = {}) => {
  try {
    // Add committee filter parameter to indicate we want candidates assigned to this committee member
    // The backend will filter nominations based on the committee member's center
    const committeeFilters = { ...filters, committee_assigned: true };
    const response = await privateApiClient.get(API.committee.candidates.list, { params: committeeFilters });
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch assigned candidates');
  }
};

/**
 * Get candidate details for evaluation
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Object>} Candidate details
 */
export const getCandidateForEvaluation = async (candidateId) => {
  try {
    // Check if candidateId is valid before making the API call
    if (!candidateId) {
      console.error('Invalid candidateId provided to getCandidateForEvaluation:', candidateId);
      return {
        data: {},
        error: 'Invalid nomination ID'
      };
    }
    
    console.log('Fetching candidate data for ID:', candidateId);
    
    // First try to get the candidate data directly
    try {
      // Use the proper API function to get the URL
      const response = await privateApiClient.get(API.committee.candidates.getById(candidateId));
      console.log('Candidate data response:', response.data);
      
      // Process the response data to ensure it has the expected structure
      const candidateData = response.data;
      
      // Make sure we have branchName and centerName for backward compatibility
      if (candidateData.branch && candidateData.branch.name) {
        candidateData.branchName = candidateData.branch.name;
        candidateData.branch_id = candidateData.branch.id;
      }
      
      if (candidateData.center && candidateData.center.name) {
        candidateData.centerName = candidateData.center.name;
        candidateData.center_id = candidateData.center.id;
      }
      
      // Now fetch evaluations for this nomination
      try {
        const evaluationsResponse = await privateApiClient.get(API.evaluations.base, {
          params: { nomination_id: candidateId }
        });
        
        console.log('Evaluations response:', evaluationsResponse.data);
        
        // Process evaluations to ensure they have the required structure
        const processedEvaluations = (evaluationsResponse.data || []).map((evaluation, index) => {
          // Ensure evaluator object exists
          if (!evaluation.evaluator) {
            evaluation.evaluator = {
              id: evaluation.evaluator_id || 0,
              full_name: `مقيم #${index + 1}`
            };
          }
          
          // Ensure score is available (might be in total_score instead of score)
          if (evaluation.total_score !== undefined && evaluation.score === undefined) {
            evaluation.score = evaluation.total_score;
          }
          
          // Ensure date is available
          if (!evaluation.date && evaluation.created_at) {
            evaluation.date = evaluation.created_at;
          }
          
          return evaluation;
        });
        
        return {
          data: {
            ...candidateData,
            evaluations: processedEvaluations
          }
        };
      } catch (evalError) {
        console.error('Error fetching evaluations:', evalError);
        // If evaluations fetch fails, still return the candidate data with empty evaluations
        return {
          data: {
            ...candidateData,
            evaluations: []
          }
        };
      }
    } catch (candidateError) {
      console.error('Error fetching candidate data:', candidateError);
      
      // If direct candidate fetch fails, try to get data from evaluations as fallback
      const evaluationsResponse = await privateApiClient.get(API.evaluations.base, {
        params: { nomination_id: candidateId }
      });
      
      if (evaluationsResponse.data && evaluationsResponse.data.length > 0) {
        // Extract nomination data from the first evaluation
        const nominationData = evaluationsResponse.data[0].nomination || {};
        
        // Process evaluations
        const processedEvaluations = (evaluationsResponse.data || []).map((evaluation, index) => {
          if (!evaluation.evaluator) {
            evaluation.evaluator = {
              id: evaluation.evaluator_id || 0,
              full_name: `مقيم #${index + 1}`
            };
          }
          
          if (evaluation.total_score !== undefined && evaluation.score === undefined) {
            evaluation.score = evaluation.total_score;
          }
          
          if (!evaluation.date && evaluation.created_at) {
            evaluation.date = evaluation.created_at;
          }
          
          return evaluation;
        });
        
        return {
          data: {
            ...nominationData,
            evaluations: processedEvaluations
          }
        };
      } else {
        // If all attempts fail, return empty data with error
        return {
          data: {},
          error: 'No data found for this candidate'
        };
      }
    }
  } catch (error) {
    return handleApiError(error, 'Failed to fetch candidate details');
  }
};

/**
 * Submit candidate evaluation
 * @param {Object} evaluationData - Evaluation data
 * @returns {Promise<Object>} Submitted evaluation
 */
export const submitEvaluation = async (evaluationData) => {
  try {
    const response = await privateApiClient.post(API.committee.evaluations.create, evaluationData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to submit evaluation');
  }
};

/**
 * Update candidate evaluation
 * @param {string} evaluationId - Evaluation ID
 * @param {Object} evaluationData - Updated evaluation data
 * @returns {Promise<Object>} Updated evaluation
 */
export const updateEvaluation = async (evaluationId, evaluationData) => {
  try {
    const response = await privateApiClient.put(API.committee.evaluations.update(evaluationId), evaluationData);
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to update evaluation');
  }
};

/**
 * Get evaluation by ID
 * @param {string} evaluationId - Evaluation ID
 * @returns {Promise<Object>} Evaluation details
 */
export const getEvaluationById = async (evaluationId) => {
  try {
    const response = await privateApiClient.get(API.committee.evaluations.getById(evaluationId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch evaluation details');
  }
};

/**
 * Get evaluations for a nomination
 * @param {string} nominationId - Nomination ID
 * @returns {Promise<Array>} List of evaluations
 */
export const getNominationEvaluations = async (nominationId) => {
  try {
    const response = await privateApiClient.get(API.committee.evaluations.getByNomination(nominationId));
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to fetch nomination evaluations');
  }
};

/**
 * Download candidate profile as PDF
 * @param {string} candidateId - Candidate ID
 * @returns {Promise<Blob>} PDF file as blob
 */
export const downloadCandidateProfile = async (candidateId) => {
  try {
    const response = await privateApiClient.get(
      API.committee.evaluations.downloadProfile(candidateId), 
      { responseType: 'blob' }
    );
    return response.data;
  } catch (error) {
    return handleApiError(error, 'Failed to download candidate profile');
  }
};

// Export all functions as a single object
const committeeService = {
  getDashboardData,
  getAssignedCandidates,
  getCandidateForEvaluation,
  submitEvaluation,
  updateEvaluation,
  getEvaluationById,
  getNominationEvaluations,
  downloadCandidateProfile,
};

export default committeeService;
